import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Lock, Truck, Search, Download, FileText, RefreshCw, Loader2, Package, CalendarDays, MapPin, ShieldCheck,
  Hourglass, Weight, Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import * as api from '@/services/transporterPortalApi';

const PAGE_SIZE = 50;

const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');

const TransporterPortal = () => {
  const { token } = useParams();
  const ssKey = `tp_pass_${token}`;

  const [passcode, setPasscode] = useState('');
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [transportName, setTransportName] = useState('');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dcFrom, setDcFrom] = useState('');
  const [dcTo, setDcTo] = useState('');

  const [tab, setTab] = useState('dispatched'); // 'dispatched' | 'pending'
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingLoaded, setPendingLoaded] = useState(false);

  const fetchPending = useCallback(async (pass, pageArg = 1, overrides = null) => {
    setPendingLoading(true);
    const f = overrides || { search, dc_from: dcFrom, dc_to: dcTo };
    try {
      const d = await api.getPortalPendingConsignments(token, {
        passcode: pass, search: f.search, dc_from: f.dc_from, dc_to: f.dc_to, page: pageArg, page_size: PAGE_SIZE,
      });
      setPendingRows(d.rows || []);
      setPendingTotal(d.total || 0);
      setPendingInvoiceCount(d.invoice_count || 0);
      setTransportName(d.transport_name || '');
      setPendingPage(pageArg);
      setPendingLoaded(true);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Session expired. Please enter the passcode again.');
        sessionStorage.removeItem(ssKey);
        setVerified(false);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load pending consignments');
      }
    } finally { setPendingLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, search, dcFrom, dcTo, ssKey]);

  const fetchConsignments = useCallback(async (pass, pageArg = 1, overrides = null) => {
    setLoading(true);
    const f = overrides || { search, date_from: dateFrom, date_to: dateTo, dc_from: dcFrom, dc_to: dcTo };
    try {
      const d = await api.getPortalConsignments(token, {
        passcode: pass, search: f.search, date_from: f.date_from, date_to: f.date_to,
        dc_from: f.dc_from, dc_to: f.dc_to, page: pageArg, page_size: PAGE_SIZE,
      });
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setInvoiceCount(d.invoice_count || 0);
      setTransportName(d.transport_name || '');
      setPage(pageArg);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Session expired. Please enter the passcode again.');
        sessionStorage.removeItem(ssKey);
        setVerified(false);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load consignments');
      }
    } finally { setLoading(false); }
  }, [token, search, dateFrom, dateTo, dcFrom, dcTo, ssKey]);

  // auto-restore an already-verified session on refresh
  useEffect(() => {
    const saved = sessionStorage.getItem(ssKey);
    if (saved) {
      setPasscode(saved);
      setVerified(true);
      fetchConsignments(saved, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleVerify = async () => {
    if (!passcode.trim()) { toast.error('Enter your passcode'); return; }
    setVerifying(true);
    try {
      const d = await api.verifyPortal(token, passcode.trim());
      setTransportName(d.transport_name || '');
      setVerified(true);
      sessionStorage.setItem(ssKey, passcode.trim());
      fetchConsignments(passcode.trim(), 1);
    } catch (e) {
      if (e.response?.status === 404) toast.error('This portal link is invalid or has been disabled.');
      else toast.error(e.response?.data?.detail || 'Invalid passcode');
    } finally { setVerifying(false); }
  };

  const handleDownload = (dcNumber) => {
    window.open(api.buildInvoiceUrl(token, dcNumber, passcode), '_blank', 'noopener');
  };

  const switchTab = (t) => {
    setTab(t);
    if (t === 'pending' && !pendingLoaded) fetchPending(passcode, 1);
  };

  const applyFilters = () => {
    if (tab === 'pending') fetchPending(passcode, 1);
    else fetchConsignments(passcode, 1);
  };

  const clearFilters = () => {
    setSearch(''); setDateFrom(''); setDateTo(''); setDcFrom(''); setDcTo('');
    if (tab === 'pending') fetchPending(passcode, 1, { search: '', dc_from: '', dc_to: '' });
    else fetchConsignments(passcode, 1, { search: '', date_from: '', date_to: '', dc_from: '', dc_to: '' });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pendingTotalPages = Math.max(1, Math.ceil(pendingTotal / PAGE_SIZE));

  // ---------------- Passcode gate ----------------
  if (!verified) {
    return (
      <div className="min-h-screen bg-[#1A1F2E] flex items-center justify-center px-4" data-testid="portal-login">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#2D5F4F] mx-auto flex items-center justify-center mb-4 shadow-lg">
              <Truck className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white font-display">Minister White</h1>
            <p className="text-[#E8DCC8] text-sm mt-1">Transporter Portal</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-2 mb-1 text-[#1A1F2E]">
              <Lock className="w-4 h-4" />
              <span className="font-semibold">Enter your passcode</span>
            </div>
            <p className="text-xs text-gray-500 mb-5">Use the 6-digit passcode shared with you to view your consignments.</p>
            <Label className="text-sm">Passcode</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
              placeholder="••••••"
              className="mt-1 h-12 text-center text-lg tracking-[0.4em] font-mono"
              autoFocus
              data-testid="portal-passcode-input"
            />
            <Button
              onClick={handleVerify}
              disabled={verifying}
              className="w-full h-12 mt-4 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="portal-verify-btn"
            >
              {verifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : 'View My Consignments'}
            </Button>
            <div className="flex items-center justify-center gap-1.5 mt-5 text-[11px] text-gray-400">
              <ShieldCheck className="w-3.5 h-3.5" /> Secure passcode access · your data is private
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- Consignments dashboard ----------------
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="portal-dashboard">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2D5F4F] flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold font-display leading-tight">Minister White — Transporter Portal</h1>
              <p className="text-xs text-[#E8DCC8]" data-testid="portal-transport-name">{transportName}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={() => (tab === 'pending' ? fetchPending(passcode, pendingPage) : fetchConsignments(passcode, page))} className="text-[#F9FAFB] hover:bg-white/10" data-testid="portal-refresh-btn">
            <RefreshCw className={`w-5 h-5 ${(loading || pendingLoading) ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-5 flex-wrap" data-testid="portal-tabs">
          <button
            onClick={() => switchTab('dispatched')}
            data-testid="portal-tab-dispatched"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'dispatched' ? 'bg-[#2D5F4F] text-white shadow' : 'bg-white text-[#1A1F2E] border border-[#E5DFD0] hover:bg-gray-50'}`}
          >
            <Send className="w-4 h-4" /> Dispatched
            <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === 'dispatched' ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{total}</span>
          </button>
          <button
            onClick={() => switchTab('pending')}
            data-testid="portal-tab-pending"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'pending' ? 'bg-amber-600 text-white shadow' : 'bg-white text-[#1A1F2E] border border-[#E5DFD0] hover:bg-gray-50'}`}
          >
            <Hourglass className="w-4 h-4" /> Not Yet Dispatched
            <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === 'pending' ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{pendingLoaded ? pendingTotal : '…'}</span>
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {tab === 'dispatched' ? (
            <>
              <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-blue-100 flex items-center justify-center"><Package className="w-6 h-6 text-blue-600" /></div>
                <div><p className="text-2xl font-bold text-[#1A1F2E]" data-testid="portal-stat-total">{total}</p><p className="text-xs text-gray-500">Dispatched Consignments</p></div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-green-100 flex items-center justify-center"><FileText className="w-6 h-6 text-green-600" /></div>
                <div><p className="text-2xl font-bold text-[#2D5F4F]" data-testid="portal-stat-invoices">{invoiceCount}</p><p className="text-xs text-gray-500">Invoices Available</p></div>
              </div>
            </>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-amber-100 flex items-center justify-center"><Hourglass className="w-6 h-6 text-amber-600" /></div>
                <div><p className="text-2xl font-bold text-[#1A1F2E]" data-testid="portal-stat-pending-total">{pendingTotal}</p><p className="text-xs text-gray-500">Awaiting Pickup</p></div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-green-100 flex items-center justify-center"><FileText className="w-6 h-6 text-green-600" /></div>
                <div><p className="text-2xl font-bold text-[#2D5F4F]" data-testid="portal-stat-pending-invoices">{pendingInvoiceCount}</p><p className="text-xs text-gray-500">Invoices Ready</p></div>
              </div>
            </>
          )}
          <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 flex items-center gap-3 col-span-2 sm:col-span-1">
            <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center"><Truck className="w-6 h-6 text-slate-600" /></div>
            <div><p className="text-sm font-semibold text-[#1A1F2E] truncate max-w-[160px]">{transportName}</p><p className="text-xs text-gray-500">Your Account</p></div>
          </div>
        </div>

        {tab === 'pending' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 flex items-start gap-2 text-sm text-amber-800" data-testid="portal-pending-note">
            <Hourglass className="w-4 h-4 mt-0.5 shrink-0" />
            <span>These consignments are tagged to you but not yet dispatched. Use the estimated quantity &amp; weight to plan your vehicle, and download any ready invoices to prepare LR copies in advance.</span>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 mb-5">
          <div className="flex flex-col md:flex-row gap-3 flex-wrap items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-gray-500">Search</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} placeholder={tab === 'pending' ? 'DC no., customer or place...' : 'DC no., customer, LR no. or place...'} className="pl-9" data-testid="portal-search-input" />
              </div>
            </div>
            {tab === 'dispatched' && (
              <>
                <div>
                  <Label className="text-xs text-gray-500">LR Date From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="portal-date-from" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">LR Date To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="portal-date-to" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs text-gray-500">DC Date From</Label>
              <Input type="date" value={dcFrom} onChange={(e) => setDcFrom(e.target.value)} className="mt-1" data-testid="portal-dc-from" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">DC Date To</Label>
              <Input type="date" value={dcTo} onChange={(e) => setDcTo(e.target.value)} className="mt-1" data-testid="portal-dc-to" />
            </div>
            <Button onClick={applyFilters} className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white" data-testid="portal-apply-filters-btn">
              <Search className="w-4 h-4 mr-2" />Apply
            </Button>
            {(search || dateFrom || dateTo || dcFrom || dcTo) && (
              <Button variant="outline" onClick={clearFilters} data-testid="portal-clear-filters-btn">Clear</Button>
            )}
          </div>
        </div>

        {tab === 'dispatched' ? (
        /* Dispatched table */
        <div className="bg-white rounded-xl border border-[#E5DFD0] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">DC Number</TableHead>
                  <TableHead className="font-semibold">DC Date</TableHead>
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold">Destination</TableHead>
                  <TableHead className="font-semibold">Vehicle</TableHead>
                  <TableHead className="font-semibold">LR No.</TableHead>
                  <TableHead className="font-semibold">LR Date</TableHead>
                  <TableHead className="font-semibold">Delivery</TableHead>
                  <TableHead className="font-semibold text-center">TAT</TableHead>
                  <TableHead className="font-semibold text-center">Boxes</TableHead>
                  <TableHead className="font-semibold text-center">Qty</TableHead>
                  <TableHead className="font-semibold text-center">Weight (kg)</TableHead>
                  <TableHead className="font-semibold text-center">Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={13} className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={13} className="text-center py-12 text-gray-400"><Package className="w-12 h-12 mx-auto text-gray-300 mb-2" /><p>No consignments found</p></TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.dc_number} className="hover:bg-gray-50" data-testid={`portal-row-${r.dc_number}`}>
                    <TableCell className="font-medium text-[#1A1F2E]">{r.dc_number}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap" data-testid={`portal-dc-date-${r.dc_number}`}>{fmtDate(r.dc_date)}</TableCell>
                    <TableCell className="max-w-[180px] truncate">{r.customer || '—'}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1 text-gray-600" title={[r.place, r.state].filter(Boolean).join(', ')}>
                        {(r.place || r.state) ? <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : null}
                        <span className="truncate max-w-[150px]">{[r.place, r.state].filter(Boolean).join(', ') || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{r.vehicle_number || '—'}</TableCell>
                    <TableCell className="text-sm">{r.lr_number || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.lr_date)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.delivery_date)}</TableCell>
                    <TableCell className="text-center text-sm">{r.tat_days != null ? `${r.tat_days}d` : '—'}</TableCell>
                    <TableCell className="text-center text-sm">{r.total_boxes || 0}</TableCell>
                    <TableCell className="text-center text-sm">{r.total_qty || 0}</TableCell>
                    <TableCell className="text-center text-sm">{r.total_weight_kg || 0}</TableCell>
                    <TableCell className="text-center">
                      {r.has_invoice ? (
                        <Button size="sm" variant="outline" onClick={() => handleDownload(r.dc_number)} className="border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F] hover:text-white" data-testid={`portal-download-${r.dc_number}`}>
                          <Download className="w-4 h-4 mr-1" />PDF
                        </Button>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500">Pending</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5DFD0]">
              <span className="text-sm text-gray-500" data-testid="portal-page-info">Page {page} of {totalPages} · {total} total</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => fetchConsignments(passcode, page - 1)} data-testid="portal-prev-btn">Prev</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => fetchConsignments(passcode, page + 1)} data-testid="portal-next-btn">Next</Button>
              </div>
            </div>
          )}
        </div>
        ) : (
        /* Not-yet-dispatched table */
        <div className="bg-white rounded-xl border border-[#E5DFD0] overflow-hidden" data-testid="portal-pending-table">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">DC Number</TableHead>
                  <TableHead className="font-semibold">DC Date</TableHead>
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold">Destination</TableHead>
                  <TableHead className="font-semibold text-center">Est. Qty</TableHead>
                  <TableHead className="font-semibold text-center">Est. Weight (kg)</TableHead>
                  <TableHead className="font-semibold text-center">Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingLoading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></TableCell></TableRow>
                ) : pendingRows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400"><Hourglass className="w-12 h-12 mx-auto text-gray-300 mb-2" /><p>Nothing awaiting pickup right now</p></TableCell></TableRow>
                ) : pendingRows.map((r) => (
                  <TableRow key={r.dc_number} className="hover:bg-gray-50" data-testid={`portal-pending-row-${r.dc_number}`}>
                    <TableCell className="font-medium text-[#1A1F2E]">{r.dc_number}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap" data-testid={`portal-pending-dc-date-${r.dc_number}`}>{fmtDate(r.dc_date)}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{r.customer || '—'}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1 text-gray-600" title={[r.place, r.state].filter(Boolean).join(', ')}>
                        {(r.place || r.state) ? <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : null}
                        <span className="truncate max-w-[180px]">{[r.place, r.state].filter(Boolean).join(', ') || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-sm font-medium">{r.est_qty || 0}</TableCell>
                    <TableCell className="text-center text-sm font-medium">
                      <span className="inline-flex items-center gap-1"><Weight className="w-3.5 h-3.5 text-gray-400" />{r.est_weight_kg || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {r.has_invoice ? (
                        <Button size="sm" variant="outline" onClick={() => handleDownload(r.dc_number)} className="border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F] hover:text-white" data-testid={`portal-pending-download-${r.dc_number}`}>
                          <Download className="w-4 h-4 mr-1" />PDF
                        </Button>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500" data-testid={`portal-pending-noinvoice-${r.dc_number}`}>Not yet available</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pendingTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5DFD0]">
              <span className="text-sm text-gray-500" data-testid="portal-pending-page-info">Page {pendingPage} of {pendingTotalPages} · {pendingTotal} total</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={pendingPage <= 1 || pendingLoading} onClick={() => fetchPending(passcode, pendingPage - 1)} data-testid="portal-pending-prev-btn">Prev</Button>
                <Button size="sm" variant="outline" disabled={pendingPage >= pendingTotalPages || pendingLoading} onClick={() => fetchPending(passcode, pendingPage + 1)} data-testid="portal-pending-next-btn">Next</Button>
              </div>
            </div>
          )}
        </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6 flex items-center justify-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5" /> Invoices sync automatically from Minister White every 30 minutes.
        </p>
      </main>
    </div>
  );
};

export default TransporterPortal;
